import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ClientListItem } from 'src/admin/users/types';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { CreateFamilyPayload, useCreateFamily } from '../hooks/useCreateFamily';
import { useUpdateFamily } from '../hooks/useUpdateFamily';
import { ReassignAdvisorConfirmModal } from '../components/ReassignAdvisorConfirmModal';
import {
  clientsOfOtherAdvisers,
  roleModelPayloadFromInProgressFamilyV2,
  toggleMember,
  V2MemberRoles,
} from './dataHelpers';
import NameAndRolesStep from './NameAndRolesStep';
import SelectMembersStep from './SelectMembersStep';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { CreateFamilySidebar } from './CreateFamilySidebar';

type FamilyFlowProps = {
  mode: 'create' | 'edit';
  familyId?: string;
  initialMembers?: ClientListItem[];
  initialName?: string;
  initialRoles?: V2MemberRoles;
  initialAdvisorUserId?: string;
  editIntent?: 'roles' | 'name';
};

export function FamilyFlow({
  mode,
  familyId,
  initialMembers = [],
  initialName = '',
  initialRoles = {},
  initialAdvisorUserId,
  editIntent = 'roles',
}: FamilyFlowProps) {
  const navigate = useNavigate();
  const { showConfirmation } = useGlobalConfirmation();
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { createFamily, isLoading: isCreating } = useCreateFamily();
  const { updateFamily, isLoading: isUpdating } = useUpdateFamily();
  // Editing starts on the details screen (name + roles/relationships); member
  // selection is reached by stepping back. Creating starts on member selection.
  const [step, setStep] = useState<'select' | 'details'>(mode === 'edit' ? 'details' : 'select');
  const [selectedMembers, setSelectedMembers] = useState<ClientListItem[]>(initialMembers);
  const [selectedAdvisor, setSelectedAdvisor] = useState<string | null>(
    initialAdvisorUserId ?? null,
  );
  const [pendingPayload, setPendingPayload] = useState<CreateFamilyPayload | null>(null);

  const isEdit = mode === 'edit';
  const isSaving = isCreating || isUpdating;
  const actionLabel = isEdit ? 'Save changes' : 'Create family';
  const isNameChange = isEdit && editIntent === 'name';
  const detailsEditable = !isNameChange;
  const cancelPath = isEdit && familyId ? `/family/${familyId}/profile` : '/clients/families';
  // Editing name/roles starts on the details screen, so "back" returns to the
  // family profile. Creating reaches details from member selection, so "back"
  // steps back to that selection.
  const detailsBack = isEdit ? () => navigate(cancelPath) : () => setStep('select');
  const relationshipsTitle = isNameChange ? 'Change family name' : undefined;
  const rolesTitle = isEdit && !isNameChange ? 'Manage roles' : relationshipsTitle;
  const advisorUserId = selectedAdvisor ?? currentUser?.id ?? '';
  const canChooseAdvisor = isSystemAdmin || isServiceAdmin;
  const conflictingMembers = clientsOfOtherAdvisers(selectedMembers, advisorUserId);

  const persistFamily = async (payload: CreateFamilyPayload) => {
    if (isEdit && familyId) {
      return updateFamily(familyId, payload);
    }
    return createFamily(payload);
  };

  const submitFamily = async (payload: CreateFamilyPayload) => {
    try {
      const result = await persistFamily(payload);
      Mixpanel.track(isEdit ? MixpanelEvents.FAMILY_UPDATED : MixpanelEvents.FAMILY_CREATED);
      if (isEdit) {
        showConfirmation(isNameChange ? 'Family name changed' : 'Successfully updated');
      }
      const targetFamilyId = isEdit ? familyId : result?.data?.id;
      navigate(targetFamilyId ? `/family/${targetFamilyId}/profile` : '/clients/families');
    } catch (error) {
      const message = ApiError.isApiError(ApiErrorCode.CLIENT_ALREADY_IN_FAMILY, error)
        ? error.message
        : `An error occurred while ${isEdit ? 'updating' : 'creating'} the family. Please try again.`;
      notifyAndLogError(`Failed to ${isEdit ? 'update' : 'create'} family`, message, error);
    }
  };

  // Every member must belong to the family's adviser. When an admin picks clients
  // from another adviser, the family request itself moves them, so only confirm first.
  const attemptSubmit = async (payload: CreateFamilyPayload) => {
    if (conflictingMembers.length > 0) {
      setPendingPayload(payload);
      return;
    }
    await submitFamily(payload);
  };

  const handleSubmitRoles = (familyName: string, roles: V2MemberRoles) =>
    attemptSubmit(
      roleModelPayloadFromInProgressFamilyV2(
        { selectedMembers, roles, name: familyName },
        advisorUserId,
      ),
    );

  const handleConfirmReassign = async () => {
    const payload = pendingPayload;
    setPendingPayload(null);
    if (payload) await submitFamily(payload);
  };

  if (step === 'details') {
    return (
      <>
        <NameAndRolesStep
          selectedMembers={selectedMembers}
          onBack={detailsBack}
          onCancel={() => navigate(cancelPath)}
          onSubmit={handleSubmitRoles}
          isLoading={isSaving}
          showAdvisorPicker={canChooseAdvisor}
          advisorUserId={advisorUserId}
          onAdvisorChange={setSelectedAdvisor}
          initialFamilyName={initialName}
          initialRoles={initialRoles}
          actionLabel={actionLabel}
          title={rolesTitle}
          rolesEditable={detailsEditable}
        />
        <SidebarContent>
          <SiestaBarLayout>
            <CreateFamilySidebar step="details" />
          </SiestaBarLayout>
        </SidebarContent>
        <ReassignAdvisorConfirmModal
          opened={pendingPayload !== null}
          conflictingMembers={conflictingMembers}
          targetAdvisorUserId={advisorUserId}
          actionDescription={isEdit ? 'Saving this family' : 'Creating this family'}
          confirmLabel={isEdit ? 'Reassign and save' : 'Reassign and create family'}
          isLoading={isSaving}
          onConfirm={handleConfirmReassign}
          onCancel={() => setPendingPayload(null)}
        />
      </>
    );
  }

  return (
    <>
      <SelectMembersStep
        title={isEdit ? 'Edit family members' : 'Select family members'}
        selectedMembers={selectedMembers}
        onToggle={client => setSelectedMembers(previous => toggleMember(previous, client))}
        onCancel={() => navigate(cancelPath)}
        onBack={() => navigate(-1)}
        onContinue={() => setStep('details')}
        continueDisabled={selectedMembers.length < 2}
        continueDisabledTooltip="Select at least 2 members to create a family"
      />
      <SidebarContent>
        <SiestaBarLayout>
          <CreateFamilySidebar step="select" />
        </SiestaBarLayout>
      </SidebarContent>
    </>
  );
}

export default function CreateFamilyPage() {
  const location = useLocation();
  const initialMembers = (location.state as { initialMembers?: ClientListItem[] } | null)
    ?.initialMembers;
  return <FamilyFlow mode="create" initialMembers={initialMembers} />;
}
